import { Component, Input, Output, EventEmitter, Inject, OnInit } from '@angular/core';
import { MoneyInputDirective } from '../../../../utils/directives/money-input.directive';
import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';
import { FeesService } from '../../../../domain/services/fees/fees.service';
import { FeeModel } from '../../../../domain/models/fee.model';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogModule, MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { ReturnModel, CustomFeeAllowanceModel } from '../../../../domain/models/return.model';
import { CasheringService } from '../../../../domain/services/cashering/cashering.service';
import { FeeType, AllowanceType, CreateFeeRequestDto, CreateAllowanceRequestDto } from '../../../../domain/models/cashering.model';

@Component({
  selector: 'app-return-fees-allowances',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule
  ],
  templateUrl: './return-fees-allowances.component.html',
  styleUrls: ['./return-fees-allowances.component.css']
})
export class ReturnFeesAllowancesComponent implements OnInit {
  @Input() returnData!: ReturnModel;
  /** true = a charge was added (host should close and reload); false = toggled/removed. */
  @Output() feesUpdated = new EventEmitter<boolean>();
  /** The fee/allowance definitions configured in the system (admin catalog). */
  catalogFees: FeeModel[] = [];

  constructor(
    private dialog: MatDialog,
    private casheringService: CasheringService,
    private feesService: FeesService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.feesService.getFees().subscribe({
      next: fees => { this.catalogFees = fees ?? []; },
      error: () => { this.catalogFees = []; }
    });
  }

  /**
   * Staff-added lines that match no catalog row. Read from the return's fees/allowances:
   * the old customFees/customAllowances fields were never sent, so these never listed.
   */
  get customFees(): { id: number; name: string; amount: number }[] {
    return this.customLines((this.returnData as any).fees, 'Fee');
  }

  get customAllowances(): { id: number; name: string; amount: number }[] {
    return this.customLines((this.returnData as any).allowances, 'Allowance');
  }

  private customLines(list: any[] | undefined, type: string): { id: number; name: string; amount: number }[] {
    const catalogNames = new Set(this.catalogFees.filter(f => f.type === type).map(f => f.name));
    return (list || [])
      .filter(x => x.type === 'CUSTOM' && !catalogNames.has(x.customName))
      .map(x => ({ id: x.id, name: x.customName, amount: x.amount }));
  }

  /** The applied line on this return that matches a catalog definition (by name). */
  appliedFor(def: FeeModel): any {
    const list = def.type === 'Allowance'
      ? ((this.returnData as any).allowances || [])
      : ((this.returnData as any).fees || []);
    const byName = list.find((x: any) => (x.customName || x.name) === def.name);
    if (byName) { return byName; }
    // Engine/standard lines are typed but named differently (e.g. "Lien Filing Fee" for the
    // catalog "Lien Fee"): match them by type so the switch never offers a duplicate charge.
    const type = this.standardType(def);
    return type ? list.find((x: any) => x.type === type) : undefined;
  }

  private standardType(def: FeeModel): string | undefined {
    const n = (def.name || '').toLowerCase();
    if (def.type === 'Allowance') { return n.includes('online') ? 'ONLINE_COLLECTION' : undefined; }
    if (n.includes('lien')) { return 'LIEN_FEE'; }
    if (n.includes('late fee')) { return 'LATE_FEE'; }
    if (n.includes('nsf') || n.includes('sufficient')) { return 'NSF_FEE'; }
    return undefined;
  }

  isApplied(def: FeeModel): boolean {
    const a = this.appliedFor(def);
    return !!a && a.enabled !== false;
  }

  /** Waive/restore a catalog fee or allowance that is applied to this return. */
  toggleCatalogItem(def: FeeModel, enabled: boolean, toggle?: { checked: boolean }): void {
    const applied = this.appliedFor(def);
    if (!applied?.id) {
      // Not on this return yet: switching it on used to do nothing (QA 2026-10-06). Open the add
      // dialog with it preselected; the switch follows the return once it is added.
      if (toggle) { toggle.checked = false; }
      if (enabled) { this.openAddDialog(def.type === 'Allowance' ? 'allowance' : 'fee', def.name); }
      return;
    }
    if (def.type === 'Allowance') {
      this.casheringService.toggleAllowance(this.returnData.id, applied.id, enabled)
        .subscribe({ next: () => this.changed(), error: e => this.failed(e) });
    } else {
      this.toggleFee(applied.id, enabled);
    }
  }

  toggleFee(feeId: number, enabled: boolean): void {
    this.casheringService.toggleFee(this.returnData.id, feeId, enabled).subscribe(
      () => this.changed(),
      (error: any) => this.failed(error)
    );
  }

  /** Enable/disable (waive) a standard fee, resolving its real id from the return's fees. */
  toggleStandardFee(type: string, enabled: boolean): void {
    const fee = ((this.returnData as any).fees || []).find((f: any) => f.type === type);
    if (fee?.id) {
      this.toggleFee(fee.id, enabled);
    }
    // No existing fee line of this type (e.g. enabling a fee that was never applied):
    // standard auto-fees are added by the engine, so nothing to toggle here.
  }

  openNewFeeDialog(): void { this.openAddDialog('fee'); }

  openNewAllowanceDialog(): void { this.openAddDialog('allowance'); }

  private openAddDialog(type: 'fee' | 'allowance', name?: string): void {
    const dialogRef = this.dialog.open(AddFeeAllowanceDialogComponent, {
      width: '400px',
      data: {
        type,
        returnId: this.returnData.id,
        catalog: this.catalogFees.filter(f => f.type === (type === 'fee' ? 'Fee' : 'Allowance')),
        name,
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.feesUpdated.emit(true); // added: the host closes so the return page reloads and shows it
      }
    });
  }

  private changed(): void {
    this.feesUpdated.emit(false);
  }

  private failed(error: any): void {
    this.snackBar.open(error?.error?.message || error?.error?.error || 'Could not update the fees on this return.',
      'Close', { duration: 6000 });
  }

  deleteCustomFee(feeId: number): void {
    this.casheringService.deleteFee(this.returnData.id, feeId).subscribe(
      () => this.changed(),
      (error: any) => this.failed(error)
    );
  }

  deleteCustomAllowance(allowanceId: number): void {
    this.casheringService.deleteAllowance(this.returnData.id, allowanceId).subscribe(
      () => this.changed(),
      (error: any) => this.failed(error)
    );
  }
}

// Add Fee/Allowance Dialog Component
@Component({
  selector: 'app-add-fee-allowance-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    FormsModule,
    MoneyInputDirective
  ],
  template: `
    <h2 mat-dialog-title>Add New {{ data.type === 'fee' ? 'Fee' : 'Allowance' }}</h2>
    <mat-dialog-content>
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>{{ data.type === 'fee' ? 'Fee' : 'Allowance' }}</mat-label>
        <mat-select [(ngModel)]="name">
          <mat-option *ngFor="let item of data.catalog" [value]="item.name">{{ item.name }}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Amount</mat-label>
        <input matInput type="number" appMoney [(ngModel)]="amount" step="0.01" placeholder="0.00">
        <span matPrefix>$&nbsp;</span>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>
        <mat-icon>arrow_back</mat-icon>
        BACK
      </button>
      <button mat-raised-button color="primary" (click)="save()" [disabled]="!isValid()">
        <mat-icon>save</mat-icon>
        SAVE
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .full-width {
      width: 100%;
      margin-bottom: 16px;
    }

    mat-dialog-content {
      min-height: 150px;
    }

    button {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  `]
})
export class AddFeeAllowanceDialogComponent {
  name: string = '';
  amount: number = 0;

  constructor(
    private dialogRef: MatDialogRef<AddFeeAllowanceDialogComponent>,
    private casheringService: CasheringService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: { type: 'fee' | 'allowance', returnId: number, catalog: FeeModel[], name?: string }
  ) {
    this.name = data.name ?? '';
  }

  isValid(): boolean {
    return this.name.trim().length > 0 && this.amount > 0;
  }

  save(): void {
    if (!this.isValid()) return;

    let saveObservable: Observable<any>;
    if (this.data.type === 'fee') {
      const feeRequest: CreateFeeRequestDto = { type: FeeType.CUSTOM, customName: this.name, amount: this.amount };
      saveObservable = this.casheringService.addFee(this.data.returnId, feeRequest);
    } else {
      const allowanceRequest: CreateAllowanceRequestDto = { type: AllowanceType.CUSTOM, customName: this.name, amount: this.amount };
      saveObservable = this.casheringService.addAllowance(this.data.returnId, allowanceRequest);
    }

    saveObservable.subscribe(
      () => {
        this.dialogRef.close(true);
      },
      (error: any) => {
        // Was console-only: a refused save looked like a dead button (QA 2026-10-06).
        this.snackBar.open(error?.error?.message || error?.error?.error || 'Could not save.', 'Close', { duration: 6000 });
      }
    );
  }
}
